/**
 * Canonical "can the user edit this property value?" predicate.
 *
 * This is the single-property edit rule shared by every surface that offers
 * an edit affordance for an EXISTING entity's property — the record
 * overview's edit mode and the list table's inline cell editing. If a
 * property renders as editable in one of those surfaces and read-only in
 * another, that's a bug; both must call this helper.
 *
 * Semantics:
 * - `value_write_access` of `internal` means the SYSTEM writes the value
 *   (e.g. `ahoy_updated_at`); `none` means nobody sets it directly. Either
 *   way, offering an input invites an edit the backend will reject.
 * - `value_update_access` of `none` marks a property immutable after
 *   creation (settable on create, frozen afterwards) — the update path this
 *   predicate guards.
 *
 * This is deliberately NOT the same question as:
 * - create-form inclusion (`value_write_access === 'all'` only — update
 *   access is irrelevant when the entity doesn't exist yet, see
 *   `genericCreateConfig.ts`);
 * - bulk-update eligibility (additionally excludes `is_unique_value` and
 *   read-restricted properties, see `useUpdatableFields.ts`);
 * - display eligibility (a read concern — internal-write properties still
 *   render, see `cardConfig.ts`).
 * Keep those rules where they are; don't fold them into this predicate.
 *
 * Permission and entity-level gates (`hasPermission(entityType,
 * 'ahoy_property_updated')`, the registry config's `readOnly`) are the
 * caller's responsibility — they depend on React context this pure
 * predicate must not reach for.
 */

/** The two schema access fields the predicate reads. Structurally satisfied
 *  by both `usePropertySchema.PropertyTypeSchema` and
 *  `SchemaClient.PropertyType`. */
export interface PropertyEditabilityAccess {
  value_write_access: 'all' | 'internal' | 'none';
  value_update_access: 'all' | 'none';
}

/** Whether the schema permits a user to edit this property on an existing
 *  entity. */
export function isPropertyEditable(schema: PropertyEditabilityAccess): boolean {
  return (
    schema.value_write_access === 'all' && schema.value_update_access === 'all'
  );
}

/**
 * Field types PropertyInput has an editor for — the case labels of the switch
 * in `components/properties/PropertyInput.tsx`. Anything else falls through
 * to its "Unknown field type" error box, so forms that CHOOSE which
 * properties to offer (create forms, inline editors) should exclude
 * properties failing hasPropertyEditor() rather than render a
 * guaranteed-broken input (e.g. internal json blobs).
 *
 * Note this is NOT the backend FieldType.type_use_access flag — that governs
 * which field types users may create NEW property types of (datetime,
 * textarea, boolean are 'internal' there yet fully editable).
 *
 * Keep in sync with the switch in PropertyInput.
 */
const EDITABLE_FIELD_TYPES = new Set([
  'text',
  'email',
  'phone_number',
  'percentage',
  'number',
  'date',
  'datetime',
  'boolean',
  'select',
  'timezone',
  'language',
  'domain',
  'url',
  'currency',
  'multi_currency',
  'textarea',
  'markdown',
  'manifest',
  'ahoy_report_query',
  'formula',
  'permissions',
  'permission_group',
  'org_type_name',
  'event_type',
  'entity_type',
  'association_type',
  'field_type',
  'property_type',
  'icon',
  'color',
  'working_hours_schedule',
  'location_type',
  'web_source',
]);

/** Whether PropertyInput can render an editor for this field type. */
export function hasPropertyEditor(fieldTypeName: string): boolean {
  return EDITABLE_FIELD_TYPES.has(fieldTypeName);
}
